<template>
  <div
    class="d-inline-flex align-items-center chips-status"
    role="group"
    aria-label="Filtros por status"
  >
    <span class="text-sm text-gray mr-3">
      <font-awesome-icon icon="filter" />
    </span>
    <template v-for="option in options">
      <hr
        v-if="option.type === 'divider'"
        :key="option.key + '_div_'"
        class="vr mr-2"
      />
      <b-button
        v-else
        :key="option.key"
        :aria-pressed="isActive(option) ? 'true' : 'false'"
        :variant="isActive(option) ? 'exitos' : 'outline-exitos'"
        class="mr-2 mb-0"
        pill
        size="sm"
        type="button"
        @click="handleChange(option)"
      >
        <font-awesome-icon
          v-if="!!option.icon"
          :icon="option.icon"
          class="px-1"
        />
        {{ option.label }}
      </b-button>
    </template>
  </div>
</template>

<script>
import { safeArray, size } from "@/helpers/functions/ArrayHelpers";
import { deepCopy } from "@/helpers/functions/ObjectHelpers";
import { useVModel } from "@vueuse/core";
import { defineComponent } from "vue";

export default defineComponent({
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },
  model: {
    event: "change",
    prop: "value",
  },
  emits: ["change"],
  setup(props, { emit }) {
    const modelValue = useVModel(props, "value", emit, {
      eventName: "change",
    });

    const options = [
      {
        label: "Todas",
        all: true,
        key: "all",
      },
      {
        type: "divider",
        key: "divider_1",
      },
      {
        label: "Oportunidades não encontradas",
        param: "status_extracao",
        key: "extracao_nao_encontrada_true",
        icon: "file-circle-xmark",
        value: "nao_encontrada",
      },
      {
        label: "Visíveis",
        param: "status_exibicao",
        key: "status_exibicao_visivel",
        icon: "eye",
        value: "visivel",
      },
      {
        label: "Em edição",
        param: "status_exibicao",
        key: "status_exibicao_edicao",
        icon: "pen-to-square",
        value: "edicao",
      },
      {
        label: "Arquivadas",
        param: "status_exibicao",
        key: "status_exibicao_arquivado",
        icon: "box-archive",
        value: "arquivado",
      },
      {
        label: "Status não definido",
        param: "status_exibicao",
        key: "status_exibicao_null",
        icon: "circle-question",
        value: "sem_status",
      },
    ];

    function isMultipleParam(param) {
      const filterByParam = (option) =>
        !option.all && option.type !== "divider" && option.param === param;
      const filteredOption = options.filter(filterByParam);

      return size(filteredOption) > 1;
    }

    function isOptionActive({ param, value }) {
      const currentValue = modelValue.value?.[param];
      const isMultiple = isMultipleParam(param);
      const hasOneActive = (selectedValue) => selectedValue === value;

      if (isMultiple) return safeArray(currentValue).some(hasOneActive);

      return currentValue === value;
    }

    function isActive(option) {
      function isCurrentActive(currentOption) {
        return (
          !currentOption.all &&
          currentOption.type !== "divider" &&
          isOptionActive(currentOption)
        );
      }

      if (option.all) return !options.some(isCurrentActive);

      return isOptionActive(option);
    }

    function handleChange(option) {
      if (option.all) {
        const emptyFilters = {
          status_extracao: null,
          status_exibicao: [],
        };

        modelValue.value = {
          ...(deepCopy(modelValue.value) || {}),
          ...emptyFilters,
        };
        return;
      }

      const { param, value } = option;
      const currentModel = modelValue.value || {};
      const nextModel = { ...currentModel };
      const isMultiple = isMultipleParam(param);
      const existsOption = (selectedValue) => selectedValue === value;

      if (isMultiple) {
        const selectedValues = Array.isArray(currentModel[param])
          ? [...currentModel[param]]
          : [];

        const selectedIndex = selectedValues.findIndex(existsOption);
        const exists = selectedIndex >= 0;

        if (exists) {
          selectedValues.splice(selectedIndex, 1);
        } else {
          selectedValues.push(value);
        }

        nextModel[param] = selectedValues;
      } else {
        nextModel[param] = currentModel[param] === value ? null : value;
      }

      modelValue.value = nextModel;
    }

    return {
      modelValue,
      options,
      handleChange,
      isActive,
    };
  },
});
</script>
